import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getSynchronousLocalStorage } from '../lib/utils/synchronousLocalStorage';
import {
  fetchPluginUpdateState,
  refreshPluginUpdateState,
  startPluginUpdateDownload,
  type PluginUpdateLifecycle,
  type PluginUpdateSnapshot,
} from '../api/grpc/pluginUpdateClient';

// ============================================================================
// State / Actions
// ============================================================================
interface PluginUpdateState {
  /** Lifecycle of the last completed check (UNKNOWN until the first read). */
  state: PluginUpdateLifecycle;
  installedVersion: string;
  latestVersion: string;
  channel: string;
  sha256: string;
  sizeBytes: number;
  releaseNotesUrl: string;
  downloadUrl: string;
  lastCheckedAtUtc: string;
  errorMessage: string;
  // ---- install lifecycle (DOWNLOADING → VERIFYING → READY_TO_APPLY / INSTALL_FAILED)
  downloadProgressPercent: number;
  stagedVersion: string;
  failureReason: string;
  canRetry: boolean;
  isBelowMinSupported: boolean;
  minSupportedVersion: string;
  autoDownloadEnabled: boolean;
  // ---- persisted user decisions
  /**
   * Latest version the user has dismissed. Persisted so dismissing `1.2.0`
   * stays dismissed across reloads, while a newer `1.3.0` still surfaces.
   */
  dismissedVersion: string | null;
  /** Staged version whose ready-to-apply banner the user dismissed. Persisted. */
  dismissedStagedVersion: string | null;
  /**
   * Installed version last observed by the user. Persisted so a post-restart
   * change (new installedVersion) can raise the "updated to X" toast exactly
   * once. Empty until the very first snapshot is seen.
   */
  lastSeenInstalledVersion: string;
  // ---- session-only flags (never persisted)
  /** True while the user has dismissed the current install failure. */
  failureDismissed: boolean;
  /** True while the user has dismissed the below-minimum modal this session. */
  belowMinModalDismissed: boolean;
  /** Non-empty version → show the post-restart success toast. */
  postRestartToastVersion: string;
  /** True once a state has been read (or attempted) this session. Not persisted. */
  initialized: boolean;
  /** True while a check/refresh/download RPC is in flight. Not persisted. */
  isChecking: boolean;
}

interface PluginUpdateActions {
  /** Replace the observable state from a fresh snapshot (also marks initialized). */
  applySnapshot: (snapshot: PluginUpdateSnapshot) => void;
  /** Read the cached state once per session (no-op if already initialized/in-flight). */
  ensureChecked: () => Promise<void>;
  /** Force an immediate plugin-side manifest re-check and apply the result. */
  refresh: () => Promise<void>;
  /** Trigger the plugin to download+stage the advertised update (also Retry). */
  startDownload: () => Promise<void>;
  /** Hide the update-available prompt for the current latest version only. */
  dismissCurrent: () => void;
  /** Hide the ready-to-apply banner for the current staged version only. */
  dismissStaged: () => void;
  /** Hide the install-failed banner until the failure reason changes. */
  dismissFailure: () => void;
  /** Hide the below-minimum modal for this session (not persisted). */
  dismissBelowMinModal: () => void;
  /** Record the current installed version and clear the post-restart toast. */
  acknowledgeInstalledVersion: () => void;
  /** Reset volatile (non-persisted) state. Primarily for tests. */
  reset: () => void;
}

type PluginUpdateStore = PluginUpdateState & PluginUpdateActions;

type PersistedFields =
  | 'dismissedVersion'
  | 'dismissedStagedVersion'
  | 'lastSeenInstalledVersion';

const initialVolatileState: Omit<PluginUpdateState, PersistedFields> = {
  state: 'UNKNOWN',
  installedVersion: '',
  latestVersion: '',
  channel: '',
  sha256: '',
  sizeBytes: 0,
  releaseNotesUrl: '',
  downloadUrl: '',
  lastCheckedAtUtc: '',
  errorMessage: '',
  downloadProgressPercent: 0,
  stagedVersion: '',
  failureReason: '',
  canRetry: false,
  isBelowMinSupported: false,
  minSupportedVersion: '',
  autoDownloadEnabled: false,
  failureDismissed: false,
  belowMinModalDismissed: false,
  postRestartToastVersion: '',
  initialized: false,
  isChecking: false,
};

/** Raw snapshot → observable fields (no dismissal / toast bookkeeping). */
function snapshotToFields(snapshot: PluginUpdateSnapshot) {
  return {
    state: snapshot.state,
    installedVersion: snapshot.installedVersion,
    latestVersion: snapshot.latestVersion,
    channel: snapshot.channel,
    sha256: snapshot.sha256,
    sizeBytes: snapshot.sizeBytes,
    releaseNotesUrl: snapshot.releaseNotesUrl,
    downloadUrl: snapshot.downloadUrl,
    lastCheckedAtUtc: snapshot.lastCheckedAtUtc,
    errorMessage: snapshot.errorMessage,
    downloadProgressPercent: snapshot.downloadProgressPercent,
    stagedVersion: snapshot.stagedVersion,
    failureReason: snapshot.failureReason,
    canRetry: snapshot.canRetry,
    isBelowMinSupported: snapshot.isBelowMinSupported,
    minSupportedVersion: snapshot.minSupportedVersion,
    autoDownloadEnabled: snapshot.autoDownloadEnabled,
    initialized: true,
    isChecking: false,
  };
}

/**
 * Pure reducer: fold a fresh snapshot into the current state, applying the two
 * cross-snapshot rules that the raw field copy cannot express on its own:
 *
 *  - Failure dismissal resets when a NEW failure reason arrives, so a fresh
 *    failure re-surfaces even after the previous one was dismissed.
 *  - The post-restart toast fires once when a previously-seen installed version
 *    changes (plugin reinstalled + Revit restarted). The very first snapshot
 *    just remembers the version without a toast.
 */
function computeApplied(
  current: PluginUpdateState,
  snapshot: PluginUpdateSnapshot
): Partial<PluginUpdateState> {
  const failureDismissed =
    snapshot.failureReason !== current.failureReason ? false : current.failureDismissed;

  const incoming = snapshot.installedVersion;
  let lastSeenInstalledVersion = current.lastSeenInstalledVersion;
  let postRestartToastVersion = current.postRestartToastVersion;
  if (incoming) {
    if (!lastSeenInstalledVersion) {
      // First run: remember, no toast.
      lastSeenInstalledVersion = incoming;
    } else if (lastSeenInstalledVersion !== incoming) {
      postRestartToastVersion = incoming;
    }
  }

  return {
    ...snapshotToFields(snapshot),
    failureDismissed,
    lastSeenInstalledVersion,
    postRestartToastVersion,
  };
}

export const usePluginUpdateStore = create<PluginUpdateStore>()(
  persist(
    (set, get) => ({
      ...initialVolatileState,
      dismissedVersion: null,
      dismissedStagedVersion: null,
      lastSeenInstalledVersion: '',

      applySnapshot: (snapshot) => set((s) => computeApplied(s, snapshot)),

      ensureChecked: async () => {
        if (get().initialized || get().isChecking) {
          return;
        }

        set({ isChecking: true });
        const snapshot = await fetchPluginUpdateState();

        // A null snapshot means no gRPC runtime (browser dev) — stay UNKNOWN so
        // the prompt remains hidden and the app never crashes outside Revit.
        // The post-await initialized guard prevents clobbering a state that was
        // seeded synchronously (e.g. the e2e seam) while the RPC was in flight.
        if (get().initialized) {
          set({ isChecking: false });
          return;
        }

        if (snapshot) {
          get().applySnapshot(snapshot);
        } else {
          set({ initialized: true, isChecking: false });
        }
      },

      refresh: async () => {
        set({ isChecking: true });
        const snapshot = await refreshPluginUpdateState();
        if (snapshot) {
          get().applySnapshot(snapshot);
        } else {
          set({ isChecking: false });
        }
      },

      startDownload: async () => {
        set({ isChecking: true });
        const snapshot = await startPluginUpdateDownload();
        if (snapshot) {
          get().applySnapshot(snapshot);
        } else {
          set({ isChecking: false });
        }
      },

      dismissCurrent: () => {
        const { latestVersion } = get();
        if (latestVersion) {
          set({ dismissedVersion: latestVersion });
        }
      },

      dismissStaged: () => {
        const { stagedVersion } = get();
        if (stagedVersion) {
          set({ dismissedStagedVersion: stagedVersion });
        }
      },

      dismissFailure: () => set({ failureDismissed: true }),

      dismissBelowMinModal: () => set({ belowMinModalDismissed: true }),

      acknowledgeInstalledVersion: () =>
        set((s) => ({
          lastSeenInstalledVersion: s.installedVersion,
          postRestartToastVersion: '',
        })),

      reset: () => set({ ...initialVolatileState }),
    }),
    {
      name: 'pipeex-plugin-update-store',
      storage: createJSONStorage(getSynchronousLocalStorage),
      // Only user decisions + the last-seen installed version survive reloads.
      // Lifecycle/version/progress fields are re-read from the plugin every
      // session; session-only dismissals (failure, below-min modal) reset.
      partialize: (state) => ({
        dismissedVersion: state.dismissedVersion,
        dismissedStagedVersion: state.dismissedStagedVersion,
        lastSeenInstalledVersion: state.lastSeenInstalledVersion,
      }),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

/**
 * The prompt is shown only when the plugin reports a newer version AND the user
 * has not already dismissed that exact latest version. Dismissing `1.2.0`
 * leaves `dismissedVersion === '1.2.0'`, so a later `1.3.0` (different string)
 * re-shows the prompt.
 */
export const selectShouldShowUpdatePrompt = (state: PluginUpdateStore): boolean =>
  state.state === 'UPDATE_AVAILABLE' &&
  !!state.latestVersion &&
  state.dismissedVersion !== state.latestVersion;

export const useShouldShowUpdatePrompt = () =>
  usePluginUpdateStore(selectShouldShowUpdatePrompt);

/** Which banner (if any) the prompt should render for the current lifecycle. */
export type UpdateBannerMode =
  | 'hidden'
  | 'update-available'
  | 'downloading'
  | 'verifying'
  | 'ready-to-apply'
  | 'install-failed';

export const selectUpdateBannerMode = (state: PluginUpdateStore): UpdateBannerMode => {
  switch (state.state) {
    case 'DOWNLOADING':
      return 'downloading';
    case 'VERIFYING':
      return 'verifying';
    case 'READY_TO_APPLY':
      return state.stagedVersion && state.dismissedStagedVersion === state.stagedVersion
        ? 'hidden'
        : 'ready-to-apply';
    case 'INSTALL_FAILED':
      return state.failureDismissed ? 'hidden' : 'install-failed';
    case 'UPDATE_AVAILABLE':
      return !!state.latestVersion && state.dismissedVersion !== state.latestVersion
        ? 'update-available'
        : 'hidden';
    default:
      return 'hidden';
  }
};

export const useUpdateBannerMode = () => usePluginUpdateStore(selectUpdateBannerMode);

/** The persistent below-minimum alert shows for as long as the plugin is below min. */
export const selectShowBelowMinAlert = (state: PluginUpdateStore): boolean =>
  state.isBelowMinSupported;

export const useShowBelowMinAlert = () => usePluginUpdateStore(selectShowBelowMinAlert);

/** The blocking below-minimum modal shows until dismissed this session. */
export const selectShowBelowMinModal = (state: PluginUpdateStore): boolean =>
  state.isBelowMinSupported && !state.belowMinModalDismissed;

export const useShowBelowMinModal = () => usePluginUpdateStore(selectShowBelowMinModal);

/** Non-empty → show the "updated to X" post-restart toast. */
export const selectPostRestartToastVersion = (state: PluginUpdateStore): string =>
  state.postRestartToastVersion;

export const usePostRestartToastVersion = () =>
  usePluginUpdateStore(selectPostRestartToastVersion);
